import axios from 'axios'
import { useI18n } from '../composables/useI18n'

/** 后端未返回 msg 时的本地化兜底文案 */
const fallback = (key: string) => useI18n().t(key)

export interface RegisterInfo {
  registerEnable: boolean
  requireEmail: boolean
  requirePhone: boolean
}

export interface AgreementInfo {
  userAgreement: string
  privacyAgreement: string
  rechargeAgreement: string
}

/** 读取用户协议、隐私协议与充值服务协议正文（登录/注册页、充值页用） */
export const getAgreementInfo = async (): Promise<AgreementInfo> => {
  const res = await axios.post('/api/agreement/info')
  return res.data?.data ?? { userAgreement: '', privacyAgreement: '', rechargeAgreement: '' }
}

/** 读取注册开关（登录页用） */
export const getRegisterInfo = async (): Promise<RegisterInfo> => {
  const res = await axios.post('/api/register/info')
  return (
    res.data?.data ?? { registerEnable: false, requireEmail: false, requirePhone: false }
  )
}

/** 发送注册邮箱验证码 */
export const sendRegisterEmailCode = async (payload: {
  email: string
  captchaToken?: string
}): Promise<void> => {
  const res = await axios.post('/api/register/emailCode', payload)
  if (res.data.code !== 0) throw new Error(res.data.msg || fallback('common.sendFailed'))
}

/** 发送注册手机验证码 */
export const sendRegisterPhoneCode = async (payload: {
  phone: string
  captchaToken?: string
}): Promise<void> => {
  const res = await axios.post('/api/register/phoneCode', payload)
  if (res.data.code !== 0) throw new Error(res.data.msg || fallback('common.sendFailed'))
}

/** 用户注册（需先通过滑块验证） */
export const register = async (payload: {
  username: string
  password: string
  email?: string
  emailCode?: string
  phone?: string
  phoneCode?: string
  captchaToken?: string
  agreed: boolean
}): Promise<void> => {
  const res = await axios.post('/api/register', payload)
  if (res.data.code !== 0) throw new Error(res.data.msg || fallback('common.registerFailed'))
}

/** 通过令牌重置密码 */
export const resetPassword = async (token: string, newPassword: string): Promise<void> => {
  const res = await axios.post('/api/password/reset', { token, newPassword })
  if (res.data.code !== 0) throw new Error(res.data.msg || fallback('common.resetFailed'))
}

/** 发送找回密码邮箱验证码（需先通过滑块验证） */
export const sendPasswordEmailCode = async (payload: {
  email: string
  captchaToken?: string
}): Promise<void> => {
  const res = await axios.post('/api/password/emailCode', payload)
  if (res.data.code !== 0) throw new Error(res.data.msg || fallback('common.sendFailed'))
}

/** 发送找回密码手机验证码（需先通过滑块验证） */
export const sendPasswordPhoneCode = async (payload: {
  phone: string
  captchaToken?: string
}): Promise<void> => {
  const res = await axios.post('/api/password/phoneCode', payload)
  if (res.data.code !== 0) throw new Error(res.data.msg || fallback('common.sendFailed'))
}

/** 凭邮箱或手机验证码重置密码 */
export const resetPasswordByCode = async (payload: {
  email?: string
  phone?: string
  code: string
  newPassword: string
}): Promise<void> => {
  const res = await axios.post('/api/password/resetByCode', payload)
  if (res.data.code !== 0) throw new Error(res.data.msg || fallback('common.resetFailed'))
}
